import { createContext, useContext } from 'react';

import type {
  Cryptocurrency,
  Currency,
  EnabledWallet,
  FiatCurrency,
  PrivateFiatWalletAccount,
  Rarity,
} from '#src/__generated__/globalTypes.ts';

import type {
  CurrentUserQuery_currentUser,
  CurrentUserQuery_currentUser_userSettings,
} from './types.ts';

type UserSettingsWithoutInitialCardDrop = Omit<
  CurrentUserQuery_currentUser_userSettings,
  'initialCardDrop'
>;

export type CurrentUser = Omit<CurrentUserQuery_currentUser, 'userSettings'> & {
  userSettings: UserSettingsWithoutInitialCardDrop;
};

type CurrentUserFiatWalletAccountDepositBankAccount =
  (CurrentUserQuery_currentUser['myAccounts'][number]['accountable'] & {
    __typename: 'PrivateFiatWalletAccount';
  })['depositBankAccount'];

type CurrentUserFiatWalletAccount = Pick<
  PrivateFiatWalletAccount,
  | 'id'
  | 'firstName'
  | 'lastName'
  | 'dob'
  | 'nationalityCode'
  | 'state'
  | 'countryOfResidenceCode'
  | 'kycStatus'
  | 'kycRefusedReason'
> & {
  depositBankAccount: CurrentUserFiatWalletAccountDepositBankAccount;
} & {
  publicInfo: Pick<PrivateFiatWalletAccount['publicInfo'], 'currency'>;
};

type CurrentUserContext = {
  currentUser: CurrentUser | null | undefined;
  currency: Currency;
  fiatCurrency: FiatCurrency;
  cryptocurrency: Cryptocurrency;
  // [SOLANA-MIGRATION]
  // TODO: Handle/rename displayEth with SOLANA balance
  displayEth: boolean;
  refetch: () => Promise<any>;
  loading: boolean;
  blockchainCardsCount: number;
  rarities: Rarity[];
  fiatWalletAccountable: CurrentUserFiatWalletAccount | null;
  walletPreferences: {
    enabledWallets?: EnabledWallet[];
    showCryptoWallet: boolean;
    showFiatWallet: boolean;
    onlyShowFiatCurrency: boolean;
    hasMigratedAndSetupWallets: boolean;
  };
  emailConfirmationNeeded: boolean;
  currentDeviceConfirmationNeeded: boolean;
  isMe: (arg?: { slug?: string } | string | null) => boolean;
};

export const CurrentUserContext = createContext<CurrentUserContext | null>(
  null
);

export const useCurrentUserContext = () => useContext(CurrentUserContext)!;
